import React, { useEffect, useState, useRef } from 'react';
import { SparklesIcon, SendIcon, Loader2Icon, ChevronDownIcon, ChevronUpIcon, Trash2Icon, WandSparklesIcon, ImagePlusIcon, XIcon } from 'lucide-react';
import { callGPTAPI } from '../utils/api';
interface GptCodeAssistantProps {
  mode: 'form' | 'email';
  currentCode: {
    html: string;
    css?: string;
    js?: string;
  };
  onCodeGenerated: (code: {
    html: string;
    css?: string;
    js?: string;
  }) => void;
}
interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}
interface AttachedImage {
  id: string;
  name: string;
  dataUrl: string;
}
const FORM_INSTRUCTIONS = `You are an expert frontend developer specializing in HTML signup forms. Your job is to generate clean, professional, production-ready signup form code.

RULES:
1. Return code in EXACTLY this format with three fenced code blocks labeled html, css, and js:
\`\`\`html
(your HTML here)
\`\`\`
\`\`\`css
(your CSS here)
\`\`\`
\`\`\`js
(your JavaScript here, if needed)
\`\`\`
2. The HTML block must contain ONLY the form markup — just the <form> element and its contents. Do NOT include <!DOCTYPE>, <html>, <head>, <body>, <link>, or <style> tags. Those are handled separately.
3. The HTML must contain a <form> element with proper input fields using name attributes
4. Common field names: email, first_name, last_name, phone, company, message
5. Always include a submit button with type="submit"
6. The CSS block contains ALL the styling. Use class names that match the classes in your HTML. The CSS will be injected into a <style> tag automatically.
7. Use modern, clean styling. Make it look professional and polished
8. The JS block can be empty if no custom behavior is needed — still include the empty block
9. Do NOT include any explanation text — ONLY the three code blocks
10. Forms should be responsive and mobile-friendly
11. Include proper placeholder text and labels for accessibility
12. If the user asks for adjustments, you MUST return the COMPLETE updated code (all three blocks) with the requested changes applied. Do NOT return the same code unchanged.
13. NEVER put CSS inside the HTML block. ALL styles go in the CSS block.
14. NEVER include <style> tags in the HTML block — put all CSS in the css block.`;
const EMAIL_INSTRUCTIONS = `You are an expert email HTML developer. Your job is to generate email-compatible HTML that renders correctly across all major email clients (Gmail, Outlook, Apple Mail, Yahoo).

RULES:
1. Return code in EXACTLY this format with one fenced code block labeled html:
\`\`\`html
(your complete email HTML here)
\`\`\`
2. Use TABLE-BASED layouts — email clients don't support flexbox/grid
3. Use inline styles — email clients strip <style> blocks inconsistently
4. Set a max-width container (typically 600px) centered with margin: 0 auto
5. Use web-safe fonts with fallbacks: Arial, Helvetica, sans-serif
6. Include proper DOCTYPE and html/head/body structure
7. All images must use absolute URLs and have alt text
8. Use role="presentation" on layout tables
9. Include cellpadding="0" cellspacing="0" on all tables
10. Support the merge variable {{confirmation_link}} for the confirmation button/link
11. Support {{team_name}} for the team/brand name
12. Support any form field variables like {{first_name}}, {{email}}, etc.
13. Do NOT include any explanation text — ONLY the html code block
14. Make the design professional, clean, and visually appealing
15. If the user asks for adjustments, you MUST return the COMPLETE updated HTML with the requested changes applied. Do NOT return the same code unchanged.`;
function parseCodeBlocks(response: string): {
  html: string;
  css?: string;
  js?: string;
} {
  const result: {
    html: string;
    css?: string;
    js?: string;
  } = {
    html: ''
  };
  // Extract HTML block
  const htmlMatch = response.match(/```html\s*\n([\s\S]*?)```/i);
  if (htmlMatch) {
    result.html = htmlMatch[1].trim();
  }
  // Extract CSS block
  const cssMatch = response.match(/```css\s*\n([\s\S]*?)```/i);
  if (cssMatch) {
    result.css = cssMatch[1].trim();
  }
  // Extract JS/JavaScript block
  const jsMatch = response.match(/```(?:js|javascript)\s*\n([\s\S]*?)```/i);
  if (jsMatch) {
    result.js = jsMatch[1].trim();
  }
  // Fallback: if no code blocks found, treat entire response as HTML
  if (!result.html && !result.css && !result.js) {
    // Try to find any generic code block
    const genericMatch = response.match(/```\s*\n([\s\S]*?)```/i);
    if (genericMatch) {
      result.html = genericMatch[1].trim();
    } else {
      result.html = response.trim();
    }
  }
  return result;
}
function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substring(2, 6);
}
export function GptCodeAssistant({
  mode,
  currentCode,
  onCodeGenerated
}: GptCodeAssistantProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [attachedImages, setAttachedImages] = useState<AttachedImage[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);
  // Use a ref to always have the latest currentCode, avoiding stale closures
  const currentCodeRef = useRef(currentCode);
  useEffect(() => {
    currentCodeRef.current = currentCode;
  }, [currentCode]);
  useEffect(() => {
    if (messagesEndRef.current && messages.length > 0) {
      messagesEndRef.current.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }, [messages]);
  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  }, [prompt]);
  const buildPrompt = (userMessage: string): string => {
    // Use ref to get the absolute latest code from the editor
    const latestCode = currentCodeRef.current;
    // For follow-up prompts, include only the user's previous requests (not raw GPT responses)
    // This avoids confusing GPT with old code blocks in conversation history
    let conversationContext = '';
    const userMessages = messages.filter(m => m.role === 'user');
    if (userMessages.length > 0) {
      conversationContext = '\n\nPREVIOUS REQUESTS FROM USER:\n';
      for (const msg of userMessages.slice(-4)) {
        conversationContext += `- ${msg.content}\n`;
      }
      conversationContext += '\nThe code below already reflects those previous requests. Now apply the NEW request.\n';
    }
    // Always include the current code from the editor so GPT modifies the latest version
    let codeContext = '';
    const hasExistingCode = latestCode.html.trim() || latestCode.css?.trim() || latestCode.js?.trim();
    if (hasExistingCode) {
      codeContext = '\n\nCURRENT CODE IN THE EDITOR (modify this code according to the user request below):\n';
      if (latestCode.html.trim()) {
        codeContext += '```html\n' + latestCode.html + '\n```\n';
      }
      if (mode === 'form') {
        if (latestCode.css?.trim()) {
          codeContext += '```css\n' + latestCode.css + '\n```\n';
        }
        if (latestCode.js?.trim()) {
          codeContext += '```js\n' + latestCode.js + '\n```\n';
        }
      }
    }
    return `${conversationContext}${codeContext}\nNEW USER REQUEST (apply these changes to the code above): ${userMessage}`;
  };
  const processImageFiles = (files: File[]) => {
    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    if (!imageFiles.length) return;
    const remaining = 3 - attachedImages.length;
    const toProcess = imageFiles.slice(0, remaining);
    toProcess.forEach(file => {
      const reader = new FileReader();
      reader.onload = ev => {
        const dataUrl = ev.target?.result as string;
        setAttachedImages(prev => [...prev, {
          id: generateId(),
          name: file.name,
          dataUrl
        }]);
      };
      reader.readAsDataURL(file);
    });
  };
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    processImageFiles(files);
    e.target.value = '';
  };
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragOver(true);
    }
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) {
      setIsDragOver(false);
    }
  };
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    processImageFiles(files);
  };
  // Paste image from clipboard (e.g. Ctrl+V screenshot)
  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = Array.from(e.clipboardData.items);
    const imageItems = items.filter(item => item.type.startsWith('image/'));
    if (!imageItems.length) return;
    e.preventDefault();
    imageItems.forEach(item => {
      const file = item.getAsFile();
      if (file) processImageFiles([file]);
    });
  };
  const removeImage = (id: string) => {
    setAttachedImages(prev => prev.filter(img => img.id !== id));
  };
  const handleSubmit = async () => {
    const trimmedPrompt = prompt.trim();
    if (!trimmedPrompt && attachedImages.length === 0 || isLoading) return;
    const userMessage: Message = {
      id: generateId(),
      role: 'user',
      content: trimmedPrompt || '(image attached)',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setPrompt('');
    setError(null);
    setIsLoading(true);
    const imagesToSend = [...attachedImages];
    setAttachedImages([]);
    try {
      const instructions = mode === 'form' ? FORM_INSTRUCTIONS : EMAIL_INSTRUCTIONS;
      const fullPromptText = buildPrompt(trimmedPrompt || '(see attached image — recreate it as code)');
      // Format prompt for OpenAI Responses API content types:
      // - No images → plain string (original behavior)
      // - With images → Responses API array with input_text / input_image types
      const promptPayload: any = imagesToSend.length === 0 ? fullPromptText : [{
        type: 'input_text',
        text: fullPromptText
      }, ...imagesToSend.map(img => ({
        type: 'input_image',
        image_url: img.dataUrl
      }))];
      const result = await callGPTAPI(instructions, promptPayload);
      const assistantMessage: Message = {
        id: generateId(),
        role: 'assistant',
        content: 'Code updated and applied to editor.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, assistantMessage]);
      const parsedCode = parseCodeBlocks(result);
      onCodeGenerated(parsedCode);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate code';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };
  const handleClearHistory = () => {
    setMessages([]);
    setError(null);
  };
  const modeLabel = mode === 'form' ? 'Signup Form' : 'Email Template';
  return <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden" data-id="element-833">
      {/* Toggle Header */}
      <button onClick={() => setIsExpanded(!isExpanded)} className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors" data-id="element-834">
        <div className="flex items-center gap-2.5" data-id="element-835">
          <div className="flex items-center justify-center w-7 h-7 rounded-md bg-purple-100" data-id="element-836">
            <WandSparklesIcon className="h-4 w-4 text-purple-600" data-id="element-837" />
          </div>
          <div data-id="element-838">
            <span className="text-sm font-medium text-gray-900" data-id="element-839">
              AI Code Assistant
            </span>
            <span className="text-xs text-gray-500 ml-2" data-id="element-840">
              Generate {modeLabel} code with GPT
            </span>
          </div>
        </div>
        {isExpanded ? <ChevronUpIcon className="h-4 w-4 text-gray-400" data-id="element-841" /> : <ChevronDownIcon className="h-4 w-4 text-gray-400" data-id="element-842" />}
      </button>

      {/* Expanded Content */}
      {isExpanded && <div className="border-t border-gray-200 relative" onDragEnter={handleDragEnter} onDragLeave={handleDragLeave} onDragOver={handleDragOver} onDrop={handleDrop} data-id="element-843">
          {/* Drag-over overlay */}
          {isDragOver && <div className="absolute inset-0 z-10 bg-purple-50/90 border-2 border-dashed border-purple-400 rounded-b-lg flex flex-col items-center justify-center gap-2 pointer-events-none" data-id="element-844">
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center" data-id="element-845">
                <ImagePlusIcon className="h-6 w-6 text-purple-600" data-id="element-846" />
              </div>
              <p className="text-sm font-medium text-purple-700" data-id="element-847">
                Drop images here
              </p>
              <p className="text-xs text-purple-500" data-id="element-848">
                Up to {3 - attachedImages.length} more image
                {3 - attachedImages.length !== 1 ? 's' : ''}
              </p>
            </div>}

          {/* Messages Area */}
          {messages.length > 0 && <div className="max-h-64 overflow-y-auto px-4 py-3 space-y-3 bg-gray-50/50" data-id="element-849">
              {messages.map(msg => <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-850">
                  <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${msg.role === 'user' ? 'bg-gray-900 text-white' : 'bg-white border border-gray-200 text-gray-700'}`} data-id="element-851">
                    {msg.role === 'assistant' ? <div className="flex items-start gap-2" data-id="element-852">
                        <SparklesIcon className="h-3.5 w-3.5 text-purple-500 mt-0.5 flex-shrink-0" data-id="element-853" />
                        <span className="text-xs text-green-700 font-medium" data-id="element-854">
                          Code applied to editor ✓
                        </span>
                      </div> : <p className="whitespace-pre-wrap" data-id="element-855">{msg.content}</p>}
                  </div>
                </div>)}
              {isLoading && <div className="flex justify-start" data-id="element-856">
                  <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 flex items-center gap-2" data-id="element-857">
                    <Loader2Icon className="h-3.5 w-3.5 text-purple-500 animate-spin" data-id="element-858" />
                    <span className="text-xs text-gray-500" data-id="element-859">
                      Generating code...
                    </span>
                  </div>
                </div>}
              <div ref={messagesEndRef} data-id="element-860" />
            </div>}

          {/* Error */}
          {error && <div className="mx-4 mt-3 bg-red-50 border border-red-200 rounded-md p-3 flex items-start justify-between gap-3" data-id="element-861">
              <p className="text-xs text-red-700 flex-1" data-id="element-862">{error}</p>
              <button onClick={handleSubmit} disabled={isLoading} className="flex-shrink-0 px-2.5 py-1 text-xs font-medium text-red-700 bg-red-100 rounded-md hover:bg-red-200 transition-colors disabled:opacity-50" data-id="element-863">
                Retry
              </button>
            </div>}

          {/* Input Area */}
          <div className="p-4 space-y-3" data-id="element-864">
            {messages.length === 0 && <div className="space-y-2" data-id="element-865">
                <p className="text-xs text-gray-500" data-id="element-866">
                  Describe what you want and GPT will generate the code. Attach
                  screenshots by dropping them here, pasting (Ctrl+V), or using
                  the image button. Refine with follow-up messages.
                </p>
                {/* Quick suggestions */}
                <div className="flex flex-wrap gap-1.5" data-id="element-867">
                  {mode === 'form' ? <>
                      <button onClick={() => setPrompt('Create a clean newsletter signup form with email, first name, and last name fields')} className="px-2.5 py-1 text-xs text-gray-600 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors" data-id="element-868">
                        Newsletter signup
                      </button>
                      <button onClick={() => setPrompt('Create a contact form with name, email, phone, and message fields with a modern dark theme')} className="px-2.5 py-1 text-xs text-gray-600 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors" data-id="element-869">
                        Contact form
                      </button>
                      <button onClick={() => setPrompt('Create a minimal email-only subscription form with inline button')} className="px-2.5 py-1 text-xs text-gray-600 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors" data-id="element-870">
                        Minimal subscribe
                      </button>
                    </> : <>
                      <button onClick={() => setPrompt('Create a professional confirmation email with a prominent confirm button using {{confirmation_link}}, greeting with {{first_name}}, and {{team_name}} branding')} className="px-2.5 py-1 text-xs text-gray-600 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors" data-id="element-871">
                        Confirmation email
                      </button>
                      <button onClick={() => setPrompt('Create a welcome email template with a hero image area, welcome message for {{first_name}}, and a CTA button')} className="px-2.5 py-1 text-xs text-gray-600 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors" data-id="element-872">
                        Welcome email
                      </button>
                      <button onClick={() => setPrompt('Create a minimal, text-focused confirmation email with {{confirmation_link}} and {{team_name}}')} className="px-2.5 py-1 text-xs text-gray-600 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors" data-id="element-873">
                        Minimal confirmation
                      </button>
                    </>}
                </div>
              </div>}

            {/* Attached image thumbnails */}
            {attachedImages.length > 0 && <div className="flex flex-wrap gap-2" data-id="element-874">
                {attachedImages.map(img => <div key={img.id} className="relative group" data-id="element-875">
                    <img src={img.dataUrl} alt={img.name} className="h-16 w-16 object-cover rounded-lg border border-gray-200" data-id="element-876" />
                    <button onClick={() => removeImage(img.id)} className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-gray-800 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" title="Remove image" data-id="element-877">
                      <XIcon className="h-2.5 w-2.5" data-id="element-878" />
                    </button>
                    <div className="absolute bottom-0 left-0 right-0 bg-black/50 rounded-b-lg px-1 py-0.5" data-id="element-879">
                      <p className="text-[9px] text-white truncate" data-id="element-880">
                        {img.name}
                      </p>
                    </div>
                  </div>)}
                {attachedImages.length < 3 && <button onClick={() => fileInputRef.current?.click()} className="h-16 w-16 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center text-gray-400 hover:border-purple-400 hover:text-purple-500 transition-colors" title="Add another image" data-id="element-881">
                    <ImagePlusIcon className="h-5 w-5" data-id="element-882" />
                  </button>}
              </div>}

            {/* Hidden file input */}
            <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleImageSelect} data-id="element-883" />

            <div className="flex items-end gap-2" data-id="element-884">
              <div className="flex-1 relative" data-id="element-885">
                <textarea ref={textareaRef} value={prompt} onChange={e => setPrompt(e.target.value)} onKeyDown={handleKeyDown} onPaste={handlePaste} placeholder={messages.length === 0 ? mode === 'form' ? 'Describe the signup form you want...' : 'Describe the email template you want...' : 'Describe adjustments...'} rows={1} disabled={isLoading} className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm resize-none pr-3 py-2.5 disabled:opacity-50 disabled:bg-gray-50" data-id="element-886" />
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0" data-id="element-887">
                {/* Image upload button */}
                <button onClick={() => fileInputRef.current?.click()} disabled={isLoading || attachedImages.length >= 3} className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed" title={attachedImages.length >= 3 ? 'Max 3 images' : 'Attach screenshot'} data-id="element-888">
                  <ImagePlusIcon className="h-4 w-4" data-id="element-889" />
                </button>
                {messages.length > 0 && <button onClick={handleClearHistory} disabled={isLoading} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50" title="Clear conversation" data-id="element-890">
                    <Trash2Icon className="h-4 w-4" data-id="element-891" />
                  </button>}
                <button onClick={handleSubmit} disabled={isLoading || !prompt.trim() && attachedImages.length === 0} className="p-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" title="Generate code" data-id="element-892">
                  {isLoading ? <Loader2Icon className="h-4 w-4 animate-spin" data-id="element-893" /> : <SendIcon className="h-4 w-4" data-id="element-894" />}
                </button>
              </div>
            </div>
            <p className="text-[11px] text-gray-400" data-id="element-895">
              Enter to send · Shift+Enter for new line · Drop or paste (Ctrl+V)
              screenshots
            </p>
          </div>
        </div>}
    </div>;
}